import { type MockStore, getMockStore } from '@hostingportal/mock-runtime'
import { type CapabilityError, HostingPortalError } from '@hostingportal/schemas'
import { z } from 'zod'
import type { CapabilityDef, CapabilitySurface, MockContext } from './types'

/**
 * Runtime selector. Reads from globalThis at dispatch time so tests and
 * Storybook can override per-render.
 */
export type HostingPortalRuntime = 'mock' | 'live'

let runtime: HostingPortalRuntime =
  (typeof globalThis !== 'undefined' &&
    (globalThis as { __HOSTINGPORTAL_RUNTIME__?: HostingPortalRuntime })
      .__HOSTINGPORTAL_RUNTIME__) ||
  (typeof import.meta !== 'undefined' &&
    (import.meta as { env?: { VITE_HOSTINGPORTAL_RUNTIME?: HostingPortalRuntime } }).env
      ?.VITE_HOSTINGPORTAL_RUNTIME) ||
  'mock'

export function setRuntime(mode: HostingPortalRuntime): void {
  runtime = mode
}

export function getRuntime(): HostingPortalRuntime {
  return runtime
}

export interface DispatchOptions {
  surface?: CapabilitySurface
  /** When true, runs the capability's mock against a transactional shadow of the store. */
  dryRun?: boolean
  /** Correlation id for log threading; auto-generated if omitted. */
  correlationId?: string
  /** Auth scopes attached to the caller. Falsy = ['*'] (mock dev mode). */
  scopes?: string[]
}

/**
 * Single entry point. UI, agent, MCP, CLI — every surface calls this. The
 * dispatcher validates input + output via Zod, picks the mock or live executor,
 * and wraps errors in HostingPortalError.
 */
export async function dispatch<Input, Output>(
  capability: CapabilityDef,
  input: Input,
  options: DispatchOptions = {},
): Promise<Output> {
  const correlationId = options.correlationId ?? newCorrelationId()
  const surface = options.surface ?? 'admin'
  const scopes = options.scopes ?? ['*']

  // Permission gate. Mock mode also enforces — gives us a real surface to test.
  const missing = capability.permissions.filter(
    (scope) => !scopes.includes('*') && !scopes.includes(scope),
  )
  if (missing.length > 0) {
    throw new HostingPortalError({
      code: 'forbidden',
      message: `Missing scopes: ${missing.join(', ')}`,
      correlationId,
      retryable: false,
    } satisfies CapabilityError)
  }

  // Validate input.
  const parsedInput = parseWithCapabilityError(capability.input, input, correlationId)

  // Execute.
  const result = await runExecutor(capability, parsedInput, {
    surface,
    correlationId,
    dryRun: options.dryRun === true,
  })

  // Validate output (mock executors must return realistic shapes per mock-runtime.md).
  return parseWithCapabilityError(capability.output, result, correlationId) as Output
}

interface RunArgs {
  surface: CapabilitySurface
  correlationId: string
  dryRun: boolean
}

async function runExecutor(
  capability: CapabilityDef,
  input: unknown,
  { surface, correlationId, dryRun }: RunArgs,
): Promise<unknown> {
  try {
    if (runtime === 'mock' || !capability.execute) {
      const ctx = mockContext({ surface, correlationId, dryRun })
      return await capability.mock(input, ctx)
    }
    const liveCtx = {
      workspaceId: getWorkspaceId(),
      surface,
      correlationId,
      fetch: globalThis.fetch.bind(globalThis),
    }
    return await capability.execute(input, liveCtx)
  } catch (err) {
    if (err instanceof HostingPortalError) throw err
    if (err instanceof z.ZodError) {
      throw new HostingPortalError({
        code: 'invalid_argument',
        message: err.issues[0]?.message ?? 'Invalid input',
        correlationId,
        fieldErrors: err.issues.map((i) => ({ path: i.path, message: i.message })),
        retryable: false,
      })
    }
    throw new HostingPortalError({
      code: 'internal_error',
      message: err instanceof Error ? err.message : 'Unknown error',
      correlationId,
      retryable: true,
    })
  }
}

function mockContext(args: RunArgs): MockContext {
  const fixtures = getMockStore()
  return {
    surface: args.surface,
    correlationId: args.correlationId,
    fixtures,
    async delay(ms: number) {
      if (ms <= 0) return
      await new Promise((r) => setTimeout(r, ms))
    },
    assertScope() {
      /* permissions already checked in dispatch */
    },
    maybeFail(rate: number) {
      if (rate > 0 && Math.random() < rate) {
        throw new HostingPortalError({
          code: 'simulated_failure',
          message: 'Mock runtime simulated a transient failure.',
          correlationId: args.correlationId,
          retryable: true,
        })
      }
    },
    emit(event: string, payload: unknown) {
      fixtures.bus.dispatchEvent(new CustomEvent(event, { detail: payload }))
    },
    async dryRun<T>(handler: () => Promise<T>): Promise<T> {
      if (args.dryRun) {
        return await fixtures.withShadow(handler)
      }
      return await handler()
    },
  }
}

function parseWithCapabilityError<T>(
  schema: z.ZodType<T>,
  value: unknown,
  correlationId: string,
): T {
  const parsed = schema.safeParse(value)
  if (parsed.success) return parsed.data
  throw new HostingPortalError({
    code: 'invalid_argument',
    message: parsed.error.issues[0]?.message ?? 'Schema validation failed',
    correlationId,
    fieldErrors: parsed.error.issues.map((i) => ({ path: i.path, message: i.message })),
    retryable: false,
  })
}

function newCorrelationId(): string {
  const rnd = Math.random().toString(36).slice(2, 10)
  return `cl-${Date.now().toString(36)}-${rnd}`
}

function getWorkspaceId(): string {
  const v = (globalThis as { __HOSTINGPORTAL_WORKSPACE_ID__?: string })
    .__HOSTINGPORTAL_WORKSPACE_ID__
  return v ?? 'ws_demo'
}

/** Re-export so consumers can avoid importing @hostingportal/mock-runtime just for the store type. */
export type { MockStore }
